import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../components/SEOHead';

const { 
  FiPlay, FiCheck, FiUsers, FiStar, FiArrowRight, FiX, FiMenu, FiChevronDown,
  FiBookOpen, FiVideo, FiClock, FiTarget, FiTrendingUp, FiShoppingCart
} = FiIcons;

const FreeModuleLandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);

  // Show sticky CTA after user scrolls past hero section
  useEffect(() => {
    const handleScroll = () => {
      setStickyVisible(window.scrollY > 600);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handlePurchaseCTA = () => {
    window.open('https://lp2.itskaivalya.com', '_blank');
  };

  return (
    <>
      <SEOHead 
        title="Free AI App Building Lesson - 14 Min Complete Tutorial | ItsKaivalya"
        description="Watch our complete 14-minute AI app building lesson. Learn to build real applications with V0, Supabase, and payments. No coding required."
        canonicalUrl="https://lp.itskaivalya.com/build-apps-with-ai-free-module-access"
        ogTitle="Free AI App Building Lesson - 14 Min Complete Tutorial"
        ogDescription="Watch our complete 14-minute AI app building lesson. Learn to build real applications with V0, Supabase, and payments. No coding required."
        ogUrl="https://lp.itskaivalya.com/build-apps-with-ai-free-module-access"
      />

      <div className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50">
        
        {/* Mobile-First Header */}
        <header className="sticky top-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700">
          <div className="px-4 py-3 flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <div className="h-8 w-8 bg-blue-500 rounded flex items-center justify-center text-white text-sm font-bold">
                K
              </div>
              <span className="ml-2 text-lg font-semibold">ItsKaivalya</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <button
                onClick={() => scrollToSection('free-lesson')}
                className="text-sm font-medium hover:text-blue-600 transition-colors"
              >
                Free Lesson
              </button>
              <button
                onClick={() => scrollToSection('course-content')}
                className="text-sm font-medium hover:text-blue-600 transition-colors"
              >
                Course Content
              </button>
              <button
                onClick={() => scrollToSection('instructor')}
                className="text-sm font-medium hover:text-blue-600 transition-colors"
              >
                Instructor
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
              >
                <div className="px-4 py-2 space-y-1">
                  <button
                    onClick={() => scrollToSection('free-lesson')}
                    className="block w-full text-left px-3 py-2 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    Free Lesson
                  </button>
                  <button
                    onClick={() => scrollToSection('course-content')}
                    className="block w-full text-left px-3 py-2 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    Course Content
                  </button>
                  <button
                    onClick={() => scrollToSection('instructor')}
                    className="block w-full text-left px-3 py-2 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    Instructor
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </header>

        {/* Hero Section - Mobile Optimized */}
        <section className="px-4 py-8 md:py-16">
          <div className="max-w-4xl mx-auto">
            
            {/* Free Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-6"
            >
              <div className="inline-flex items-center px-4 py-2 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 rounded-full text-sm font-medium border border-green-200 dark:border-green-800">
                <SafeIcon icon={FiStar} className="w-4 h-4 mr-2" />
                100% Free Lesson - No Strings Attached
              </div>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-center leading-tight mb-4"
            >
              Understand AI App Dev in 
              <span className="text-blue-600 dark:text-blue-400"> 14 Minutes</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg md:text-xl text-slate-600 dark:text-slate-300 text-center leading-relaxed mb-8 px-2"
            >
              Watch the free lesson below showing you how to build a real ordering app with payments, authentication, and dashboards — using AI tools. No coding required.
            </motion.p>

            {/* Key Benefits - Mobile Optimized */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
            >
              {[
                { icon: FiClock, text: "14-min free lesson" },
                { icon: FiTarget, text: "Real app development" },
                { icon: FiCheck, text: "Zero coding experience needed" }
              ].map((benefit, index) => (
                <div key={index} className="flex items-center justify-center md:justify-start p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <SafeIcon icon={benefit.icon} className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
                  <span className="text-sm md:text-base font-medium text-center md:text-left">{benefit.text}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Button - Watch Free Lesson */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <button
                onClick={() => scrollToSection('free-lesson')}
                className="w-full md:w-auto inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-lg"
              >
                Watch the Free Lesson
                <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </button>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">
                14 minutes • Complete tutorial • 100% Free
              </p>
            </motion.div>
          </div>
        </section>

        {/* Free Lesson Video Section - Mobile Optimized */}
        <section id="free-lesson" className="px-4 py-8 md:py-16 bg-slate-50 dark:bg-slate-800">
          <div className="max-w-4xl mx-auto">
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold text-center mb-3"
            >
              See What You'll Build
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-base md:text-lg text-slate-600 dark:text-slate-300 text-center mb-8 px-2"
            >
              Watch this 14-minute preview to see the complete app we'll build together
            </motion.p>

            {/* Video Container - Mobile Optimized */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="relative aspect-video bg-slate-900 rounded-lg md:rounded-xl overflow-hidden shadow-xl border-2 border-slate-200 dark:border-slate-700"
            >
              {!videoPlaying ? (
                // Video Thumbnail
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-600">
                  <div className="text-center">
                    <motion.button
                      onClick={() => setVideoPlaying(true)}
                      className="w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 mx-auto hover:bg-white/30 transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <SafeIcon icon={FiPlay} className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" />
                    </motion.button>
                    <h3 className="text-lg md:text-xl font-bold text-white mb-2">
                      AI App Building Free Lesson
                    </h3>
                    <p className="text-sm md:text-base text-white/80 px-4">
                      14-min demo of the complete app dev process
                    </p>
                  </div>
                </div>
              ) : (
                // Actual Video Embed - Updated to new URL
                <iframe
                  src="https://www.youtube.com/embed/dY2Xme2deFg?autoplay=1&rel=0&modestbranding=1"
                  title="AI App Building Free Lesson - 14 Minutes Complete Tutorial"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </motion.div>

            {/* Video Benefits */}
            <div className="mt-8">
              {/* Desktop: Show above video benefits text */}
              <div className="hidden md:block mb-8">
                <p className="text-base text-slate-600 dark:text-slate-300 text-center mb-4">
                  Build Apps, Websites &amp; Tools With AI - Free Module<br />
                  Learn to build apps with AI - No coding required
                </p>
              </div>

              {/* Benefits Grid - Both Mobile and Desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Complete step-by-step walkthrough",
                  "Real app with working payments",
                  "No coding experience required",
                  "Instant deployment to live URL"
                ].map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600"
                  >
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 dark:text-green-400 mr-3 flex-shrink-0" />
                    <span className="text-sm md:text-base font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>

              {/* Mobile: Show below video benefits text */}
              <div className="block md:hidden mt-6">
                <p className="text-sm text-slate-600 dark:text-slate-300 text-center leading-relaxed">
                  <span className="font-semibold">What you get in the course:</span><br />
                  Complete step-by-step walkthrough, real app with working payments, no coding experience required, instant deployment to live URL
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Course Content Section - Mobile Optimized */}
        <section id="course-content" className="px-4 py-8 md:py-16">
          <div className="max-w-4xl mx-auto">
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold text-center mb-3"
            >
              What's Inside The Course?
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-base md:text-lg text-slate-600 dark:text-slate-300 text-center mb-12 px-2"
            >
              Everything you need to build and deploy your first AI-powered app
            </motion.p>

            {/* Course Modules - Mobile Optimized */}
            <div className="space-y-4">
              {[
                {
                  title: "Introduction and History of Web Development",
                  duration: "08:55"
                },
                {
                  title: "Modern AI Powered App Development and AI Tools", 
                  duration: "14:55"
                },
                {
                  title: "Examples and Live Demo Building Login and Registration",
                  duration: "18:25"
                },
                {
                  title: "Backend Connection with Supabase and SQL Queries",
                  duration: "07:53"
                },
                {
                  title: "Debugging and Fixing Errors",
                  duration: "13:53"
                },
                {
                  title: "Using ChatGPT to Optimize Prompts",
                  duration: "06:29"
                },
                {
                  title: "Best Practices for Building Complex Apps",
                  duration: "03:12"
                },
                {
                  title: "Progressive Web Apps and Mobile Development",
                  duration: "13:47"
                },
                {
                  title: "Market Research and Validating Ideas",
                  duration: "03:39"
                },
                {
                  title: "Course Offering and Support for App Development",
                  duration: "10:04"
                },
                {
                  title: "Prompt Engineering",
                  duration: "01:38:39"
                },
                {
                  title: "100X Business Impact Using Custom AI Workflows",
                  duration: "24:41"
                }
              ].map((module, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white dark:bg-slate-800 rounded-lg p-4 md:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    {/* Module Number */}
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{index + 1}</span>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {/* Module Title */}
                      <h3 className="text-base md:text-lg font-semibold mb-2 leading-tight">
                        {module.title}
                      </h3>
                      
                      {/* Duration */}
                      <div className="flex items-center">
                        <SafeIcon icon={FiClock} className="w-4 h-4 text-slate-500 dark:text-slate-400 mr-2" />
                        <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                          {module.duration}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Total Duration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg md:rounded-xl border border-blue-200 dark:border-blue-800"
            >
              <h3 className="text-xl md:text-2xl font-bold mb-2">
                Total Course Duration: 3 Hours 44 Minutes
              </h3>
              <p className="text-slate-600 dark:text-slate-300 text-sm md:text-base">
                Complete comprehensive training from beginner to deployment
              </p>
            </motion.div>
          </div>
        </section>

        {/* Instructor Section - Mobile Optimized */}
        <section id="instructor" className="px-4 py-8 md:py-16 bg-slate-50 dark:bg-slate-800">
          <div className="max-w-4xl mx-auto">
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold text-center mb-12"
            >
              Meet Your Instructor
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              
              {/* Instructor Photo */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="order-2 md:order-1"
              >
                <img
                  src="https://i.ibb.co/DHSRjPSv/kaivalya-profile-photo.jpg"
                  alt="Kaivalya Deshpande - AI Development Instructor"
                  className="w-full max-w-sm mx-auto rounded-lg md:rounded-xl shadow-lg"
                />
              </motion.div>

              {/* Instructor Bio */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="order-1 md:order-2 text-center md:text-left"
              >
                <h3 className="text-xl md:text-2xl font-bold mb-4">
                  Kaivalya Deshpande
                </h3>
                <p className="text-blue-600 dark:text-blue-400 font-medium mb-6">
                  AI Development Expert & Course Creator
                </p>
                
                <div className="space-y-4 text-sm md:text-base text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                  <p>
                    I've built and deployed <strong>50+ AI-powered applications</strong> for 
                    businesses ranging from startups to established companies.
                  </p>
                  <p>
                    My mission is simple: help you build real, working apps without getting 
                    stuck in tutorial hell or spending months learning complex coding.
                  </p>
                  <p>
                    This free lesson contains the exact process I use to build apps for 
                    my clients — no theory, just practical, step-by-step implementation.
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400">50+</div>
                    <div className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Apps Built</div>
                  </div>
                  <div>
                    <div className="text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400">2,500+</div>
                    <div className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Students Taught</div>
                  </div>
                  <div>
                    <div className="text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400">4+</div>
                    <div className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Years Experience</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Final CTA Section - Mobile Optimized */}
        <section className="px-4 py-8 md:py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="max-w-3xl mx-auto text-center">
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold mb-4"
            >
              Ready to Build Your First AI App?
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg md:text-xl opacity-90 mb-8 leading-relaxed px-2"
            >
              You've seen the free lesson. Now join thousands of developers who are building real AI applications with our complete course.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              onClick={handlePurchaseCTA}
              className="w-full md:w-auto inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-lg hover:bg-slate-50"
            >
              Get the Complete Course
              <SafeIcon icon={FiShoppingCart} className="w-5 h-5 ml-2" />
            </motion.button>
            
            <p className="text-sm opacity-75 mt-4">
              Start building real apps today
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-4 py-8 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="h-8 w-8 bg-blue-500 rounded flex items-center justify-center text-white text-sm font-bold mr-2">
                K
              </div>
              <span className="text-lg font-semibold">ItsKaivalya</span>
            </div>
            <p className="text-slate-400 text-sm">
              © 2025 ItsKaivalya. Helping developers build real AI applications.
            </p>
          </div>
        </footer>

        {/* Sticky Purchase CTA - Shows after scrolling past hero */}
        {stickyVisible && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-lg z-40">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 text-sm md:text-base truncate">
                  Ready for the complete course?
                </p>
                <p className="text-xs md:text-sm text-slate-500">
                  Build real apps with step-by-step guidance
                </p>
              </div>
              <button
                onClick={handlePurchaseCTA}
                className="ml-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 md:px-6 py-3 rounded-lg transition-colors duration-200 text-sm md:text-base min-h-[44px]"
              >
                <span className="hidden sm:inline">Get Complete Course</span>
                <span className="sm:hidden">Get Course</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FreeModuleLandingPage;